/**
 * 錯誤處理輔助函數
 */

import * as Sentry from "@sentry/react";
import type { ApiError } from "@/types/error";
import { HTTP_ERROR_MESSAGES, NETWORK_ERROR_MESSAGE, DEFAULT_ERROR_MESSAGE } from "@/types/error";
import { decodeHTMLEntity } from "@/utils/utils";

// Types
export const ErrorType = {
  API_ERROR: "api_error",
  ROUTER_ERROR: "router_error",
  REACT_COMPONENT_ERROR: "react_component_error",
  RUNTIME_ERROR: "runtime_error",
  UNCAUGHT_ERROR: "uncaught_error",
} as const;

type ErrorType = (typeof ErrorType)[keyof typeof ErrorType];

/**
 * 判斷是否為已中止的請求
 */
export const isAbortedError = (error: unknown): boolean => {
  return (error as ApiError)?.isAborted === true;
};

/**
 * 判斷是否為網路錯誤（無回應）
 */
export const isNetworkError = (error: unknown): boolean => {
  const apiError = error as ApiError;
  return !apiError?.status || apiError.status === 0;
};

/**
 * 從 error 物件中提取錯誤訊息
 */
export const extractErrorMessage = (error: unknown, fallbackMessage?: string): string => {
  const apiError = error as ApiError;

  // 已中止的請求不顯示訊息
  if (isAbortedError(apiError)) {
    return "";
  }

  let message = "";

  // 優先順序 1: error.data.message
  if (apiError?.data?.message) {
    message = apiError.data.message;
  }
  // 優先順序 2: error.data.error.data.details.message (Woo Custom API，後端回傳 Error 待優化)
  else if (apiError?.data?.error?.data?.details?.message) {
    message = apiError.data.error.data.details.message;
  }
  // 優先順序 3: error.message
  else if (apiError?.message) {
    message = apiError.message;
  }
  // 優先順序 4: 根據 HTTP 狀態碼取得友善訊息
  else if (apiError?.status && HTTP_ERROR_MESSAGES[apiError.status]) {
    message = HTTP_ERROR_MESSAGES[apiError.status];
  }
  // 優先順序 5: 網路錯誤
  else if (isNetworkError(apiError)) {
    message = NETWORK_ERROR_MESSAGE;
  }
  // 優先順序 6: 使用傳入的 fallback 訊息
  else if (fallbackMessage) {
    message = fallbackMessage;
  }
  // 優先順序 7: 預設訊息
  else {
    message = DEFAULT_ERROR_MESSAGE;
  }

  // 解碼 HTML 實體並清理訊息
  return decodeHTMLEntity(message.trim());
};

/**
 * 取得錯誤的詳細描述（用於顯示在 desc 欄位）
 */
export const extractErrorDescription = (error: unknown): string | undefined => {
  const apiError = error as ApiError;

  // 如果有額外的錯誤資訊，可以放在描述中
  if (apiError?.data && typeof apiError.data === "object") {
    const data = apiError.data;
    // 如果有錯誤代碼
    if (data.code) {
      return `錯誤代碼: ${data.code}`;
    }
  }

  return undefined;
};

/**
 * 報告錯誤到 Sentry
 * (未來移到 sentry.ts 管理)
 */
export const reportErrorToSentry = (
  error: unknown,
  context?: Record<string, any>,
  errorType?: ErrorType,
) => {
  if (!isAbortedError(error)) {
    Sentry.captureException(error, {
      contexts: context ?? {},
      tags: {
        errorType: errorType ?? ErrorType.UNCAUGHT_ERROR,
      },
    });
  }
};

/**
 * 在 Sentry 捕捉 React 組件錯誤前，於 scope 上設置自定義 tag 與 context。
 *
 * @param scope - Sentry Scope 物件，用來添加錯誤標籤與上下文
 * @param error - 觸發的錯誤物件
 * @param errorInfo - React 組件錯誤相關的額外資訊（包含 component stack）
 *
 * 此函數常用於 Sentry.ErrorBoundary 組件的 beforeCapture 屬性，可針對 React 組件錯誤自定義彙整更多資訊。
 * (未來移到 sentry.ts 管理)
 */
export const handleSentryBeforeCapture = (scope, error, errorInfo) => {
  scope.setTag("errorType", ErrorType.REACT_COMPONENT_ERROR);
  scope.setContext("react", {
    componentStack: errorInfo.componentStack,
  });
};

/**
 * 判斷是否應該記錄此錯誤（已中止的請求不記錄）
 */
export const shouldLogError = (error: unknown): boolean => {
  return !isAbortedError(error);
};
